import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import Layout from '../components/Layout';
import { sapPracticeGroups } from '../data/awsSapGuideData';
import './AWSCloudFundamentalsCourse.css';
import './AWSSapPages.css';

const L = 'ABCD';
const same = (a: number[], b: number[]) => a.length === b.length && a.every((x) => b.includes(x));
const chipLabel = (label: string) => label.split(' — ')[0].replace(' Questions', '');

const AWSSAPPracticePage: React.FC = () => {
  const [group, setGroup] = useState(-1);
  const [submitted, setSubmitted] = useState<Record<string, number[]>>({});
  const [draft, setDraft] = useState<Record<string, number[]>>({});

  const total = useMemo(() => sapPracticeGroups.reduce((n, g) => n + g.questions.length, 0), []);
  const answered = Object.keys(submitted).length;
  const correct = Object.entries(submitted).filter(([k, v]) => {
    const [g, q] = k.split('-').map(Number);
    return same(v, sapPracticeGroups[g].questions[q].a);
  }).length;

  const reset = () => { setSubmitted({}); setDraft({}); };

  return (
    <Layout>
      <div className="page-hero">
        <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 6%' }}>
          <div style={{ fontSize: '0.9rem', color: 'var(--gray-600)', marginBottom: '12px' }}>
            <Link to="/programs" style={{ color: 'var(--blue)' }}>Programs</Link> /{' '}
            <Link to="/aws-sap" style={{ color: 'var(--blue)' }}>AWS Solutions Architect Professional</Link> / Practice
          </div>
          <h1>SAP-C02 Practice Questions</h1>
          <p>Scenario-based questions with instant feedback, a detailed reason for the right answer and why each wrong option fails.</p>
          <div style={{ marginTop: '16px', display: 'flex', gap: '8px', flexWrap: 'wrap', justifyContent: 'center' }}>
            <span className="section-tag">{total} questions</span>
            <span className="section-tag">{sapPracticeGroups.length} sets</span>
            <span className="section-tag orange">Single and multiple answer</span>
          </div>
        </div>
      </div>

      <div className="cf section" style={{ paddingTop: '20px' }}>
        <div className="cf-body" style={{ maxWidth: '1100px' }}>
          <div className="cf-chips">
            <button className={group === -1 ? 'on' : ''} onClick={() => setGroup(-1)}>All ({total})</button>
            {sapPracticeGroups.map((g, i) => (
              <button key={g.id} className={group === i ? 'on' : ''} onClick={() => setGroup(i)}>{chipLabel(g.label)} ({g.questions.length})</button>
            ))}
          </div>

          <div className="cf-score" style={{ top: '70px' }}>
            <span>Score: {correct} / {answered} answered (of {total})</span>
            <button onClick={reset}>Reset</button>
          </div>

          {sapPracticeGroups.map((g, gi) => {
            if (group !== -1 && group !== gi) return null;
            return (
              <section className="les" key={g.id}>
                <div className="sh">
                  <i>{g.id.startsWith('mock') ? '🧪' : '🎯'}</i>
                  <div>
                    <h2>{g.label}</h2>
                    <small>{g.id.startsWith('mock') ? `${g.questions.length} sample questions · ${g.sub.replace(/^[·\s]+|[·\s]+$/g, '').replace(/^\d+ Questions · /, '')}` : g.sub}</small>
                  </div>
                </div>
                {g.questions.map((q, qi) => {
                  const key = `${gi}-${qi}`;
                  const multi = q.a.length > 1;
                  const sub = submitted[key];
                  const done = sub !== undefined;
                  const sel = draft[key] || [];
                  const ok = done && same(sub, q.a);
                  const pick = (oi: number) => {
                    if (done) return;
                    if (!multi) setSubmitted((s) => ({ ...s, [key]: [oi] }));
                    else setDraft((d) => ({ ...d, [key]: sel.includes(oi) ? sel.filter((x) => x !== oi) : [...sel, oi] }));
                  };
                  return (
                    <div className="cf-q" key={key}>
                      <div className="cf-meta">
                        <span>{q.n}</span>
                        {q.level && <span className={q.level}>{q.level}</span>}
                        {q.domain && <span>{q.domain}</span>}
                      </div>
                      <h3>{q.q}</h3>
                      {multi && <p className="cf-hint">Select {q.a.length} answers</p>}
                      {q.o.map((o, oi) => {
                        const cls = done
                          ? (q.a.includes(oi) ? (sub.includes(oi) ? ' ok' : ' ok missed') : sub.includes(oi) ? ' no' : '')
                          : sel.includes(oi) ? ' sel' : '';
                        return (
                          <button key={oi} className={`cf-opt${cls}`} disabled={done} onClick={() => pick(oi)}>
                            {L[oi]}. {o}
                          </button>
                        );
                      })}
                      {multi && !done && (
                        <button className="btn btn-blue btn-sm" disabled={sel.length !== q.a.length} style={{ opacity: sel.length !== q.a.length ? 0.5 : 1, marginTop: '6px' }} onClick={() => setSubmitted((s) => ({ ...s, [key]: sel }))}>
                          Check answer
                        </button>
                      )}
                      {done && (
                        <div className="cf-exp">
                          <p className={ok ? 'cf-res ok' : 'cf-res no'}>
                            {ok ? '✓ Correct.' : '✗ Not quite.'} <b>Answer: {q.a.map((i) => L[i]).join(', ')}</b>
                          </p>
                          <h4>Detailed explanation</h4>
                          <p>{q.r}</p>
                          {q.w.length > 0 && (
                            <>
                              <h4>Why the other options are wrong</h4>
                              <ul className="cf-wrong">
                                {q.w.map((line, li) => {
                                  const dot = line.indexOf('. ');
                                  return <li key={li}><b>{line.slice(0, dot + 1)}</b> {line.slice(dot + 2)}</li>;
                                })}
                              </ul>
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </section>
            );
          })}

          <div className="cf-end" style={{ marginTop: '30px' }}>
            <h2>Ready for the exam?</h2>
            <p>Review the guide for any topic you missed, then retry the set.</p>
            <div style={{ marginTop: '14px', display: 'flex', gap: '10px', justifyContent: 'center', flexWrap: 'wrap' }}>
              <Link to="/aws-sap-guide" className="btn btn-outline-blue" style={{ background: '#fff' }}>Review the SAP guide</Link>
              <button className="btn btn-orange" onClick={() => { reset(); window.scrollTo({ top: 0, behavior: 'smooth' }); }}>Practise again</button>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default AWSSAPPracticePage;
